import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { catchError, finalize, of } from 'rxjs';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { type PaginatorState } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';

import { ComissaoConselheiroAtuacaoPageResponse, ComissaoConselheiroAtuacaoResponse, ComissaoEstatisticaAnaliticoPageResponse } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.model';
import { ComissaoEstatisticaService } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.service';
import { ComissaoReuniaoFiltroRequest, EComissaoReuniaoTipoPeriodoFiltro } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { DetalhamentoPendencias } from '../../gestao-administrativa/detalhamento-pendencias/detalhamento-pendencias';
import { IndicadorPendencia } from '../../gestao-administrativa/mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../../gestao-administrativa/mapa-pendencias/pendencia-linha.model';
import { mapLinhaReuniaoAnalitico } from '../ranking-reunioes/ranking-reunioes.model';
import { PainelComissoesContainer } from '../painel-comissoes/painel-comissoes';
import { RankingConselheiro, mapLinhaComissaoConselheiro, montarRankingConselheiros } from './ranking-conselheiros.model';

type TipoRankingConselheiro = 'presenca' | 'comissoes' | 'comparecimento';

interface ConfigRankingConselheiro {
    titulo: string;
    subtitulo: string;
    legendaVazio: string;
    icone: string;
    sort: string;
    isPercentual: boolean;
    sufixo: string;
    resumoLabel: string;
    valor: (linha: ComissaoConselheiroAtuacaoResponse) => number;
}

@Component({
    selector: 'app-ranking-conselheiros',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DatePicker, AvatarModule, SkeletonModule, DetalhamentoPendencias, PainelComissoesContainer],
    templateUrl: './ranking-conselheiros.html',
    styleUrls: ['../ranking-reunioes/ranking-reunioes.scss']
})
export class RankingConselheirosComponent implements OnInit {
    private readonly estatisticaService = inject(ComissaoEstatisticaService);
    private readonly route = inject(ActivatedRoute);
    private readonly cdr = inject(ChangeDetectorRef);

    private readonly configs: Record<TipoRankingConselheiro, ConfigRankingConselheiro> = {
        presenca: {
            titulo: 'Conselheiros com maior presença',
            subtitulo: 'Top 10 conselheiros por presenças (presencial + virtual) no período filtrado.',
            legendaVazio: 'Nenhum conselheiro com presença no período.',
            icone: 'pi pi-fw pi-star',
            sort: 'totalPresencas,desc',
            isPercentual: false,
            sufixo: 'presenças',
            resumoLabel: 'Maior presença',
            valor: (linha) => Number(linha.totalPresencas) || 0
        },
        comissoes: {
            titulo: 'Conselheiros em mais comissões',
            subtitulo: 'Top 10 conselheiros por número de comissões ativas.',
            legendaVazio: 'Nenhum conselheiro com comissões no período.',
            icone: 'pi pi-fw pi-sitemap',
            sort: 'qtdComissoes,desc',
            isPercentual: false,
            sufixo: 'comissões',
            resumoLabel: 'Mais comissões',
            valor: (linha) => Number(linha.qtdComissoes) || 0
        },
        comparecimento: {
            titulo: 'Conselheiros por % de comparecimento',
            subtitulo: 'Top 10 conselheiros por percentual de comparecimento no período filtrado.',
            legendaVazio: 'Nenhum conselheiro com registros no período.',
            icone: 'pi pi-fw pi-percentage',
            sort: 'percentualComparecimento,desc',
            isPercentual: true,
            sufixo: '%',
            resumoLabel: 'Maior comparecimento',
            valor: (linha) => Number(linha.percentualComparecimento) || 0
        }
    };

    tipo: TipoRankingConselheiro = 'presenca';
    escopo: string | undefined;
    dataInicioDate: Date | null = new Date(new Date().getFullYear(), 0, 1);
    dataFimDate: Date | null = new Date(new Date().getFullYear(), 11, 31);
    carregando = false;
    ranking: RankingConselheiro[] = [];

    modoAnalitico = false;
    pessoaSelecionada: RankingConselheiro | null = null;
    linhasAnalitico: PendenciaLinha[] = [];
    firstAnalitico = 0;
    rowsAnalitico = 100;
    totalRegistrosAnalitico = 0;
    carregandoAnalitico = false;

    get config(): ConfigRankingConselheiro {
        return this.configs[this.tipo];
    }

    get podio(): RankingConselheiro[] {
        return this.ranking.slice(0, 3);
    }

    get demais(): RankingConselheiro[] {
        return this.ranking.slice(3);
    }

    get totalConselheiros(): number {
        return this.ranking.length;
    }

    get maiorValor(): number {
        return this.ranking[0]?.valor ?? 0;
    }

    get periodoAnalisado(): string {
        return `${this.formatarDataExibicao(this.dataInicioDate)} a ${this.formatarDataExibicao(this.dataFimDate)}`;
    }

    ngOnInit(): void {
        const tipoRota = this.route.snapshot.data['tipoRanking'];
        this.tipo = tipoRota === 'comissoes' || tipoRota === 'comparecimento' ? tipoRota : 'presenca';
        const escopoRota = this.route.snapshot.data['escopo'];
        this.escopo = escopoRota === 'FEDERAL' || escopoRota === 'REGIONAL' ? escopoRota : undefined;
        this.carregar();
    }

    get rotuloEscopo(): string {
        if (this.escopo === 'FEDERAL') {
            return 'Federais';
        }
        if (this.escopo === 'REGIONAL') {
            return 'Regionais';
        }
        return '';
    }

    get tituloComEscopo(): string {
        return this.rotuloEscopo ? `${this.config.titulo} — ${this.rotuloEscopo}` : this.config.titulo;
    }

    carregar(): void {
        this.carregando = true;
        this.estatisticaService
            .consultarAtuacaoConselheiros(this.criarFiltro(), 0, 10, this.config.sort, this.escopo)
            .pipe(
                catchError(() => of({ content: [], totalElements: 0 } as ComissaoConselheiroAtuacaoPageResponse)),
                finalize(() => {
                    this.carregando = false;
                    // Sem isto a view continua no skeleton mesmo com carregando = false: o estado muda,
                    // a renderizacao nao. Acontecia nos rankings federais, que voltam vazios.
                    this.cdr.markForCheck();
                })
            )
            .subscribe((resp) => {
                this.ranking = montarRankingConselheiros(resp.content ?? [], this.config.valor);
                this.cdr.markForCheck();
            });
    }

    limpar(): void {
        this.dataInicioDate = new Date(new Date().getFullYear(), 0, 1);
        this.dataFimDate = new Date(new Date().getFullYear(), 11, 31);
        this.carregar();
    }

    formatarValor(item: RankingConselheiro): string {
        return this.config.isPercentual ? `${item.valor}%` : `${item.valor}`;
    }

    legendaValor(): string {
        return this.config.isPercentual ? 'comparecimento' : this.config.sufixo;
    }

    /**
     * No ranking percentual todo mundo pode empatar em 100%. Mostrar a base deixa claro que
     * "100% de 1 reunião" não é o mesmo que "100% de 12".
     */
    legendaItem(item: RankingConselheiro): string {
        if (!this.config.isPercentual) {
            return `${this.legendaValor()} · ${item.uf}`;
        }
        const base = item.base ? `de ${item.base} ${item.base === 1 ? 'reunião' : 'reuniões'}` : 'sem reuniões no período';
        return `${base} · ${item.uf}`;
    }

    abrirAnalitico(item: RankingConselheiro): void {
        if (!item.idSolicitante) {
            return;
        }
        this.pessoaSelecionada = item;
        this.modoAnalitico = true;
        this.firstAnalitico = 0;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.carregarAnalitico();
    }

    voltarRanking(): void {
        this.modoAnalitico = false;
        this.pessoaSelecionada = null;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.firstAnalitico = 0;
        this.carregandoAnalitico = false;
    }

    onPageChangeAnalitico(event: PaginatorState): void {
        this.firstAnalitico = event.first ?? 0;
        this.rowsAnalitico = event.rows ?? this.rowsAnalitico;
        this.carregarAnalitico();
    }

    get colunaUfAnalitico(): string {
        return this.tipo === 'comissoes' ? 'TIPO' : 'PRESENÇA';
    }

    get colunaIdentificadorAnalitico(): string {
        return this.tipo === 'comissoes' ? 'ESTRUTURA' : 'REUNIÃO';
    }

    get colunaSituacaoAnalitico(): string {
        return this.tipo === 'comissoes' ? 'SITUAÇÃO' : 'STATUS';
    }

    get colunaDetalheAnalitico(): string | null {
        return this.tipo === 'comissoes' ? null : 'DATA';
    }

    obterIndicadorAnalitico(): IndicadorPendencia | null {
        if (!this.pessoaSelecionada) {
            return null;
        }
        return {
            id: `conselheiro-${this.pessoaSelecionada.idSolicitante}`,
            titulo: this.pessoaSelecionada.nome,
            descricao: this.tipo === 'comissoes' ? `Comissões em que ${this.pessoaSelecionada.nome} participa.` : `Reuniões com presença de ${this.pessoaSelecionada.nome} no período filtrado.`,
            grupo: 'Operacional',
            classeGrupo: 'grupo-registro',
            quantidade: this.totalRegistrosAnalitico || null
        };
    }

    private carregarAnalitico(): void {
        const pessoa = this.pessoaSelecionada;
        if (!pessoa || !pessoa.idSolicitante) {
            return;
        }
        const rows = this.rowsAnalitico || 100;
        const page = Math.floor((this.firstAnalitico || 0) / rows);
        this.carregandoAnalitico = true;
        const consulta =
            this.tipo === 'comissoes'
                ? this.estatisticaService.consultarAnaliticoComissoesConselheiro(pessoa.idSolicitante, pessoa.uf, page, rows)
                : this.estatisticaService.consultarAnaliticoReunioesConselheiro(pessoa.idSolicitante, pessoa.uf, this.criarFiltro(), page, rows);
        consulta
            .pipe(
                catchError(() => of({ content: [], totalElements: 0 } as ComissaoEstatisticaAnaliticoPageResponse)),
                finalize(() => {
                    this.carregandoAnalitico = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe((resultado) => {
                const linhas = resultado.content ?? [];
                this.linhasAnalitico = this.tipo === 'comissoes' ? linhas.map((item) => mapLinhaComissaoConselheiro(item)) : linhas.map((item) => mapLinhaReuniaoAnalitico(item));
                this.totalRegistrosAnalitico = resultado.totalElements ?? this.linhasAnalitico.length;
                this.cdr.markForCheck();
            });
    }

    private criarFiltro(): ComissaoReuniaoFiltroRequest {
        return {
            tipoPeriodo: EComissaoReuniaoTipoPeriodoFiltro.POR_PERIODO,
            dataInicioReuniao: this.formatarData(this.dataInicioDate),
            dataFimReuniao: this.formatarData(this.dataFimDate),
            excluirStatusReuniaoExcluida: false
        };
    }

    private formatarData(data: Date | null): string | undefined {
        if (!data) {
            return undefined;
        }
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
    }

    private formatarDataExibicao(data: Date | null): string {
        if (!data) {
            return '-';
        }
        const dia = String(data.getDate()).padStart(2, '0');
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const ano = data.getFullYear();
        return `${dia}/${mes}/${ano}`;
    }
}
